import { useCallback, useEffect, useState } from 'react';

import { useTestLoginEnabled } from '@/hooks/useTestLoginEnabled';
import { validateEmail } from '@/lib/auth/constants';
import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';
import { TextArea } from '@/lib/base/forms/textarea/textarea';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { fetchWithDefaults } from '@/lib/http/client';

const TOKEN_STORAGE_KEY = 'ahoy_test_login_token';
const HELP_TEXT =
  'Dev/QA only. Creates or reuses a test user with synthetic data.';
const HEADING_TEXT = 'Test login';
const LABEL_PLACEHOLDER = 'e.g., Demo prospect';
const SCENARIO_PLACEHOLDER =
  "e.g., 'prospect asking about pricing with a follow-up meeting scheduled'";
const LABEL_MAX = 100;
const SCENARIO_MAX = 2000;

// Smart defaults applied ONLY in local development (when require_token=false).
// In qa/preview/development the modal still opens with empty fields so each
// tester picks a unique email per session and doesn't collide with shared
// fixtures. The defaults are applied once when the local config resolves —
// they will not overwrite anything the user has already typed.
const LOCAL_DEFAULT_EMAIL = 'johndoe@ahoy.harbour';
const LOCAL_DEFAULT_LABEL = 'John Doe';

function getInitialToken(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

const DEPLOY_PENDING_MESSAGE =
  'Test login endpoint not yet deployed. Wait for backend deploy to complete.';
const NETWORK_ERROR_MESSAGE =
  'Network error. Check your connection and try again.';

function friendlyErrorForStatus(status: number | undefined): string {
  if (status === undefined) {
    return NETWORK_ERROR_MESSAGE;
  }
  switch (status) {
    case 401:
      return 'Invalid token. Check your TEST_LOGIN_TOKEN.';
    case 403:
      return 'Test login is not available in this environment.';
    case 404:
      return DEPLOY_PENDING_MESSAGE;
    case 409:
      return 'This email belongs to an existing account and cannot be used for test login. Pick a different test email, or use the Reset and re-onboard button if this is your test user.';
    case 429:
      return '20-connection cap reached. Use the Reset and re-onboard button or a different email.';
    case 500:
    case 502:
    case 503:
    case 504:
      return 'Something went wrong. Check the backend logs.';
    default:
      return 'Something went wrong. Try again, and get in touch if it keeps happening.';
  }
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface TestLoginResponse {
  redirect_url: string;
}

export function TestLoginModal({ isOpen, onClose }: Props) {
  // `requireToken` is sourced from the backend `config/` endpoint:
  // - production / qa / preview / development → true (token required)
  // - local                                   → false (token field hidden, bypass)
  // Defaults to `true` (fail closed) while the config is loading or on error.
  const { requireToken } = useTestLoginEnabled();
  const [email, setEmail] = useState('');
  const [label, setLabel] = useState('');
  const [scenarioPrompt, setScenarioPrompt] = useState('');
  const [token, setToken] = useState<string>(() => getInitialToken());
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Apply local-only defaults the first time `requireToken` resolves to false.
  // The state-updater form checks `prev === ''` so we never overwrite anything
  // the user has already typed (e.g. they opened the modal, started typing,
  // and the config resolved a moment later).
  useEffect(() => {
    if (!requireToken) {
      setEmail(prev => (prev === '' ? LOCAL_DEFAULT_EMAIL : prev));
      setLabel(prev => (prev === '' ? LOCAL_DEFAULT_LABEL : prev));
    }
  }, [requireToken]);

  const handleScenarioChange = useCallback(
    (value: string) => setScenarioPrompt(value),
    []
  );

  const handleClose = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    setError(null);
    setShowResetConfirm(false);
    onClose();
  }, [isSubmitting, onClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleSubmit = useCallback(async () => {
    setError(null);

    const trimmedEmail = email.trim();
    const trimmedLabel = label.trim();
    const trimmedToken = token.trim();
    if (!trimmedEmail) {
      setError('Email is required.');
      return;
    }
    const emailError = validateEmail(trimmedEmail);
    if (emailError) {
      setError(emailError);
      return;
    }
    if (!trimmedLabel) {
      setError('Label is required.');
      return;
    }
    if (requireToken && !trimmedToken) {
      setError('Token is required.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Authorization header is omitted entirely in local (require_token=false);
      // the backend bypasses the token check in that environment.
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (requireToken) {
        headers.Authorization = `Bearer ${trimmedToken}`;
      }

      const response = await fetchWithDefaults(
        '/api/test-integration/test-login/',
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            email: trimmedEmail,
            label: trimmedLabel,
            scenario_prompt: scenarioPrompt,
          }),
        }
      );

      if (!response.ok) {
        console.error('Test login failed', { status: response.status });
        setError(friendlyErrorForStatus(response.status));
        return;
      }

      const data = (await response.json()) as TestLoginResponse;

      if (requireToken) {
        try {
          window.localStorage.setItem(TOKEN_STORAGE_KEY, trimmedToken);
        } catch {
          // localStorage unavailable — proceed without persisting.
        }
      }

      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        console.error('Test login: missing redirect_url in response', {
          receivedKeys: Object.keys(data),
        });
        setError("We couldn't complete the test login. Try again.");
      }
    } catch (err) {
      console.error('Test login network error', {
        errorType: err instanceof Error ? err.name : typeof err,
      });
      setError(friendlyErrorForStatus(undefined));
    } finally {
      setIsSubmitting(false);
    }
  }, [email, label, scenarioPrompt, token, requireToken]);

  const handleResetClick = useCallback(() => setShowResetConfirm(true), []);
  const handleResetCancel = useCallback(() => setShowResetConfirm(false), []);

  const handleResetConfirm = useCallback(async () => {
    setError(null);
    const trimmedEmail = email.trim();
    const trimmedToken = token.trim();
    if (!trimmedEmail) {
      setError(
        requireToken
          ? 'Email and token are required to reset.'
          : 'Email is required to reset.'
      );
      return;
    }
    if (requireToken && !trimmedToken) {
      setError('Email and token are required to reset.');
      return;
    }
    setIsSubmitting(true);
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (requireToken) {
        headers.Authorization = `Bearer ${trimmedToken}`;
      }
      const response = await fetchWithDefaults(
        '/api/test-integration/test-login/cleanup/',
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ email: trimmedEmail }),
        }
      );
      if (!response.ok && response.status !== 204) {
        console.error('Test login cleanup failed', { status: response.status });
        setError(friendlyErrorForStatus(response.status));
        setIsSubmitting(false);
        return;
      }
      setShowResetConfirm(false);
      setIsSubmitting(false);
      await handleSubmit();
    } catch (err) {
      console.error('Test login cleanup network error', {
        errorType: err instanceof Error ? err.name : typeof err,
      });
      setError(friendlyErrorForStatus(undefined));
      setIsSubmitting(false);
    }
  }, [email, token, handleSubmit, requireToken]);

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable={!isSubmitting}
    >
      <Modal className='max-w-md w-full'>
        <Dialog aria-label={HEADING_TEXT} hideCloseButton>
          {/* Header */}
          <div className='border-b border-secondary px-6 py-4'>
            <h2 className='text-lg font-semibold text-secondary'>
              {HEADING_TEXT}
            </h2>
            <p className='mt-1 text-xs text-tertiary'>{HELP_TEXT}</p>
          </div>

          {/* Body */}
          <div className='flex-1 overflow-y-auto px-6 py-4 space-y-4'>
            <Input
              label='Email'
              type='email'
              isRequired
              value={email}
              onChange={setEmail}
              placeholder='you@example.com'
            />

            <div className='space-y-1.5'>
              <Input
                label='Test integration label'
                isRequired
                value={label}
                onChange={setLabel}
                placeholder={LABEL_PLACEHOLDER}
                maxLength={LABEL_MAX}
              />
              <span className='text-xs text-tertiary'>
                {label.length}/{LABEL_MAX}
              </span>
            </div>

            <div className='space-y-1.5'>
              <TextArea
                label='Scenario prompt (optional)'
                value={scenarioPrompt}
                onChange={handleScenarioChange}
                placeholder={SCENARIO_PLACEHOLDER}
                maxLength={SCENARIO_MAX}
                rows={3}
              />
              <span className='text-xs text-tertiary'>
                {scenarioPrompt.length}/{SCENARIO_MAX}
              </span>
            </div>

            {requireToken && (
              <Input
                label='Token'
                type='password'
                isRequired
                value={token}
                onChange={setToken}
                placeholder='TEST_LOGIN_TOKEN'
              />
            )}

            {error && (
              <p className='text-sm text-[var(--color-text-error-primary)]'>
                {error}
              </p>
            )}

            {showResetConfirm && (
              <div className='rounded-lg border border-border-primary p-3 space-y-2'>
                <p className='text-sm text-secondary'>
                  This will permanently delete the test user and all their data.
                  Continue?
                </p>
                <div className='flex gap-2'>
                  <Button
                    color='secondary'
                    size='sm'
                    onClick={handleResetCancel}
                    isDisabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    color='primary'
                    size='sm'
                    onClick={handleResetConfirm}
                    isDisabled={isSubmitting}
                  >
                    Delete and re-onboard
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className='flex justify-between gap-3 border-t border-secondary px-6 py-4'>
            <Button
              color='secondary'
              size='md'
              onClick={handleResetClick}
              isDisabled={
                isSubmitting ||
                !email.trim() ||
                !label.trim() ||
                (requireToken && !token.trim())
              }
            >
              Reset and re-onboard
            </Button>
            <div className='flex gap-3'>
              <Button
                color='secondary'
                size='md'
                onClick={handleClose}
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                color='primary'
                size='md'
                onClick={handleSubmit}
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
              >
                {isSubmitting ? 'Logging in…' : 'Log in'}
              </Button>
            </div>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
